import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useAnimation, Variants, Easing } from "framer-motion";
import {
  ArrowRight,
  Zap,
  LayoutDashboard,
  BarChart3,
  Sparkles,
  Target,
  Workflow,
  Calendar,
  Mail,
  ClipboardPlus,
  Smartphone,
  Camera,
  TrendingUp,
  UserPlus,
  RefreshCw,
  ShieldCheck,
  HardHat,
  Building2,
  Users,
  Crown,
} from "lucide-react";
import { FaGithub, FaTwitter, FaLinkedin } from "react-icons/fa";
import { all_routes } from "../router/all_routes";
import ImageWithBasePath from "../../core/common/imageWithBasePath";

const easeOut: Easing = [0.16, 1, 0.3, 1];

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: easeOut } },
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: easeOut } },
};

interface AnimatedSectionProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}

const AnimatedSection = ({ children, className = "", delay = 0 }: AnimatedSectionProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const variants: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay, ease: easeOut } },
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variants}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const FEATURES = [
  {
    icon: ClipboardPlus,
    title: "First Task Creation",
    highlights: [
      "Convert everyday tasks into structured tasks with a simple workflow.",
      "Auto capture sender, time, and basic details; add due dates, owners, and priority in one step.",
      "Ideal for teams that already coordinate work over WhatsApp.",
    ],
  },
  {
    icon: Smartphone,
    title: "Mobile App for On the Go Operations",
    highlights: [
      "Android friendly mobile interface for field staff and supervisors.",
      "Create, update and close tasks from any location, even with low bandwidth.",
      "View assigned work, upcoming deadlines, reminders, and task history on a single screen.",
    ],
  },
  {
    icon: Camera,
    title: "Rich Media Capture (Audio, Video, Images)",
    highlights: [
      "Attach photos, short videos, and audio notes to tasks during creation or updates.",
      "Use visual evidence to confirm work done, record issues, or share site conditions.",
      "Build a complete audit trail for every task — especially useful for service calls and inspections.",
    ],
  },
  {
    icon: LayoutDashboard,
    title: "Real Time Dashboards",
    highlights: [
      "Live overview of all tasks by status, owner, team, location, and due date.",
      "Color coded views for overdue, high priority and escalated tasks.",
      "Drill down from organization level to department, branch or individual.",
    ],
  },
  {
    icon: TrendingUp,
    title: "KPI Driven Performance Management",
    highlights: [
      "Define KPIs for each team: on time completion, TAT (turnaround time), first time fix rate, visit compliance, and more.",
      "Automatically compute KPIs based on actual task data instead of manual reporting.",
      "Compare performance across months, teams, locations, and clients.",
    ],
  },
  {
    icon: Workflow,
    title: "Flexible Task Workflows",
    highlights: [
      "Configure task stages such as: New, Accepted, In Progress, On Hold, Completed, Verified.",
      "Map workflows to your business processes — service, maintenance, sales, back office, etc.",
      "Set SLAs and escalation rules for critical tasks.",
    ],
  },
];

const STEPS = [
  {
    number: "01",
    title: "Capture",
    description: "Create a task from the TaskLync app, with basic details and assignee.",
    icon: ClipboardPlus,
  },
  {
    number: "02",
    title: "Enrich",
    description: "Add photos, videos or audio notes to explain the issue or request.",
    icon: Camera,
  },
  {
    number: "03",
    title: "Assign & Start",
    description: "Assign tasks to individuals or teams; set reminders, they receive instant notifications.",
    icon: UserPlus,
  },
  {
    number: "04",
    title: "Update",
    description: "Field staff and office teams update status, add comments and additional media as work progresses.",
    icon: RefreshCw,
  },
  {
    number: "05",
    title: "Complete & Verify",
    description: "Task is marked completed, and supervisors can verify with attached proof.",
    icon: ShieldCheck,
  },
  {
    number: "06",
    title: "Measure",
    description: "Dashboards and reports convert completed tasks into KPIs and performance insights.",
    icon: BarChart3,
  },
];

const KPI_CATEGORIES = [
  {
    title: "Standard KPIs",
    icon: BarChart3,
    items: [
      "On time completion rate.",
      "Average resolution time.",
      "First time fix rate.",
      "Number of tasks per person, per day.",
      "Pending vs completed tasks by team or branch.",
    ],
  },
  {
    title: "Custom KPIs",
    icon: TrendingUp,
    items: [
      "Client specific SLAs (response time, resolution time).",
      "Visit compliance for field teams.",
      "Escalation rate and repeat issues per asset/site.",
    ],
  },
  {
    title: "Dashboards",
    icon: LayoutDashboard,
    items: [
      "Executive dashboard for owners and senior managers.",
      "Team dashboard for supervisors.",
      "Individual view for staff to monitor their own performance.",
    ],
  },
];

const FIELD_TEAM_CAPABILITIES = [
  "Receive tasks on mobile with clear location, timeline and instructions.",
  "Capture photos and videos of work completed or issues observed.",
  "Record short audio notes instead of typing long updates.",
  "Close tasks with proof of work, so managers and clients can see exactly what happened.",
];

const SERVICE_INDUSTRY_BENEFITS = [
  "Fewer missed visits and forgotten commitments.",
  "Quick evidence for warranty, service history and quality checks.",
  "Easier monitoring of outsourced or franchise teams.",
];

const PRICING_PLANS = [
  {
    name: "Starter Plan",
    users: "Up to 10 users",
    icon: Users,
    featured: false,
  },
  {
    name: "Growth Plan",
    users: "Up to 50 users",
    icon: TrendingUp,
    featured: true,
  },
  {
    name: "Enterprise Plan",
    users: "Unlimited users",
    icon: Crown,
    featured: false,
  },
];

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Insights", href: "#insights" },
  { label: "Field Teams", href: "#field-teams" },
  { label: "Pricing", href: "#pricing" },
];

const FOOTER_LINKS = {
  Product: ["Features", "Pricing", "Integrations", "Changelog"],
  Company: ["About", "Blog", "Careers", "Contact"],
  Resources: ["Documentation", "Help Center", "API", "Status"],
  Legal: ["Privacy", "Terms", "Security"],
};

const LandingPage = () => {
  useEffect(() => {
    document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="landing-page">
      {/* Floating decorative elements */}
      <div className="landing-page__decorations" aria-hidden="true">
        <div className="landing-page__decoration landing-page__decoration--1" />
        <div className="landing-page__decoration landing-page__decoration--2" />
        <div className="landing-page__decoration landing-page__decoration--3" />
      </div>

      {/* Header */}
      <motion.header
        className="landing-page__header"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="landing-page__header-inner">
          <Link to="/" className="landing-page__logo">
            <img src="assets/tl-full.png" alt="TaskLync" />
          </Link>

          <nav className="landing-page__nav">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="landing-page__nav-link"
                onClick={(e) => scrollToSection(e, link.href)}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="landing-page__header-actions">
            
            <Link to={all_routes.login} className="landing-page__cta-btn landing-page__cta-btn--header">
            Sign In 
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="landing-page__hero">
        <div className="landing-page__hero-content">
          <motion.div
            className="landing-page__hero-text"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div className="landing-page__hero-badge" variants={fadeInUp}>
              <Sparkles size={14} />
              <span>Trusted by organizations in the service industry</span>
            </motion.div>

            <motion.h1 className="landing-page__hero-title" variants={fadeInUp}>
              Empower your team with
              <span className="landing-page__hero-title-highlight"> seamless</span>
              <br />
              task management
            </motion.h1>

            <motion.p className="landing-page__hero-subtitle" variants={fadeInUp}>
              Plan, assign, and track work across your organization — from daily tasks
              to complex workflows — all in one powerful platform.
            </motion.p>

            <motion.div className="landing-page__hero-ctas" variants={fadeInUp}>
              <Link to={all_routes.login} className="landing-page__cta-btn landing-page__cta-btn--primary">
                Get Started
                <ArrowRight size={18} />
              </Link>
              <button type="button" className="landing-page__cta-btn landing-page__cta-btn--secondary">
                Book a Demo
              </button>
            </motion.div>
          </motion.div>

          <motion.div
            className="landing-page__hero-visual"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          >
            <div className="landing-page__hero-mockup">
              <div className="landing-page__hero-mockup-inner">
                <ImageWithBasePath
                  src="assets/img/bg/login-hero-task.svg"
                  alt="TaskLync Dashboard"
                  className="landing-page__hero-mockup-img"
                />
              </div>
              
              {/* Floating badges */}
              <motion.div
                className="landing-page__float-card landing-page__float-card--1"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <Zap size={14} className="landing-page__float-card-icon" />
                <span>Real-time sync</span>
              </motion.div>
              
              <motion.div
                className="landing-page__float-card landing-page__float-card--2"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              >
                <Target size={14} className="landing-page__float-card-icon" />
                <span>Smart workflows</span>
              </motion.div>
              
              <motion.div
                className="landing-page__float-card landing-page__float-card--3"
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              >
                <Calendar size={15} className="landing-page__float-card-icon" />
                <span>Track deadlines</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="landing-page__features">
        <div className="landing-page__features-inner">
          <AnimatedSection className="landing-page__section-header">
            <span className="landing-page__section-badge">Features</span>
            <h2 className="landing-page__section-title">
              Built for teams that run on
              <span> real-world operations</span>
            </h2>
            
          </AnimatedSection>

          <motion.div
            className="landing-page__features-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {FEATURES.map((feature) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  className="landing-page__feature-card"
                  variants={scaleIn}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                >
                  <div className="landing-page__feature-icon">
                    <Icon size={24} />
                  </div>
                  <h3 className="landing-page__feature-title">{feature.title}</h3>
                  <ul className="landing-page__feature-highlights">
                    {feature.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="landing-page__how-it-works">
        <div className="landing-page__how-it-works-inner">
          <AnimatedSection className="landing-page__section-header">
            <span className="landing-page__section-badge">How It Works</span>
            <h2 className="landing-page__section-title">
              Step by Step <span>Flow</span>
            </h2>
           
          </AnimatedSection>

          <div className="landing-page__steps">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <AnimatedSection
                  key={step.number}
                  className="landing-page__step"
                  delay={index * 0.15}
                >
                  <div className="landing-page__step-number">{step.number}</div>
                  <div className="landing-page__step-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="landing-page__step-title">{step.title}</h3>
                  <p className="landing-page__step-desc">{step.description}</p>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Performance Insights Section */}
      <section id="insights" className="landing-page__insights">
        <div className="landing-page__insights-inner">
          <AnimatedSection className="landing-page__section-header">
            <span className="landing-page__section-badge">Performance Insights</span>
            <h2 className="landing-page__section-title">
              Turn Tasks into <span>Performance Insights</span>
            </h2>
            <p className="landing-page__section-subtitle">
              TaskLync&apos;s KPI engine turns your task data into actionable insights.
            </p>
          </AnimatedSection>

          <motion.div
            className="landing-page__insights-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {KPI_CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={category.title}
                  className="landing-page__insights-card"
                  variants={scaleIn}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                >
                  <div className="landing-page__insights-card-header">
                    <div className="landing-page__insights-icon">
                      <Icon size={22} />
                    </div>
                    <h3 className="landing-page__insights-card-title">{category.title}</h3>
                  </div>
                  <ul className="landing-page__insights-list">
                    {category.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Field & Service Teams Section */}
      <section id="field-teams" className="landing-page__field-teams">
        <div className="landing-page__field-teams-inner">
          <AnimatedSection className="landing-page__section-header">
            <span className="landing-page__section-badge">For Field & Service Teams</span>
            <h2 className="landing-page__section-title">
              Make Field Work <span>Visible and Measurable</span>
            </h2>
            <p className="landing-page__section-subtitle">
              TaskLync is especially useful for organizations where many people work outside the office —
              technicians, service engineers, sales staff, survey teams, and support staff.
            </p>
          </AnimatedSection>

          <motion.div
            className="landing-page__field-teams-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            <motion.div
              className="landing-page__field-teams-card"
              variants={scaleIn}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              <div className="landing-page__field-teams-card-header">
                <div className="landing-page__field-teams-icon">
                  <HardHat size={22} />
                </div>
                <h3 className="landing-page__field-teams-card-title">What Field Teams Can Do</h3>
              </div>
              <ul className="landing-page__field-teams-list">
                {FIELD_TEAM_CAPABILITIES.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="landing-page__field-teams-card"
              variants={scaleIn}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              <div className="landing-page__field-teams-card-header">
                <div className="landing-page__field-teams-icon">
                  <Building2 size={22} />
                </div>
                <h3 className="landing-page__field-teams-card-title">Benefits for Service Industry</h3>
              </div>
              <ul className="landing-page__field-teams-list">
                {SERVICE_INDUSTRY_BENEFITS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="landing-page__pricing">
        <div className="landing-page__pricing-inner">
          <AnimatedSection className="landing-page__section-header">
            <span className="landing-page__section-badge">Pricing</span>
            <h2 className="landing-page__section-title">
              Simple, Transparent <span>Pricing</span>
            </h2>
            <p className="landing-page__section-subtitle">
              TaskLync is designed to be affordable for MSMEs and scalable for larger organizations.
            </p>
          </AnimatedSection>

          <motion.div
            className="landing-page__pricing-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {PRICING_PLANS.map((plan) => {
              const Icon = plan.icon;
              return (
                <motion.div
                  key={plan.name}
                  className={`landing-page__pricing-card${plan.featured ? " landing-page__pricing-card--featured" : ""}`}
                  variants={scaleIn}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                >
                  {plan.featured && (
                    <span className="landing-page__pricing-badge">Recommended</span>
                  )}
                  <div className="landing-page__pricing-icon">
                    <Icon size={24} />
                  </div>
                  <h3 className="landing-page__pricing-plan">{plan.name}</h3>
                  <p className="landing-page__pricing-users">{plan.users}</p>
                  <Link
                    to={all_routes.login}
                    className={`landing-page__cta-btn ${plan.featured ? "landing-page__cta-btn--primary" : "landing-page__cta-btn--secondary"} landing-page__pricing-cta`}
                  >
                    Get Started
                    <ArrowRight size={16} />
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>

          <AnimatedSection className="landing-page__pricing-note" delay={0.2}>
            <p>
              Each plan can be offered on a <strong>monthly</strong> or <strong>annual</strong> subscription basis,
              with special pricing for clusters, cooperatives or government linked institutions.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-page__cta-section">
        <AnimatedSection className="landing-page__cta-section-inner">
          <div className="landing-page__cta-content">
            <h2 className="landing-page__cta-title">
              Ready to transform how your team works?
            </h2>
            <p className="landing-page__cta-subtitle">
              Join thousands of teams already using TaskLync to achieve more together.
              Get started today or book a demo with our team.
            </p>
            <div className="landing-page__cta-buttons">
              <Link to={all_routes.login} className="landing-page__cta-btn landing-page__cta-btn--white">
                Get Started
                <ArrowRight size={18} />
              </Link>
              <button type="button" className="landing-page__cta-btn landing-page__cta-btn--outline">
                Book a Demo
              </button>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* Footer */}
      <footer className="landing-page__footer">
        <div className="landing-page__footer-inner">
          <div className="landing-page__footer-main">
            <div className="landing-page__footer-brand">
              <Link to="/" className="landing-page__footer-logo">
                <img src="assets/tl-full.png" alt="TaskLync" />
              </Link>
              <p className="landing-page__footer-tagline">
                Empowering teams to do their best work through seamless task management.
              </p>
              <div className="landing-page__footer-social">
                <a href="#" aria-label="Twitter"><FaTwitter size={18} /></a>
                <a href="#" aria-label="LinkedIn"><FaLinkedin size={18} /></a>
                <a href="#" aria-label="GitHub"><FaGithub size={18} /></a>
                <a href="#" aria-label="Email"><Mail size={18} /></a>
              </div>
            </div>

            <div className="landing-page__footer-links">
              {Object.entries(FOOTER_LINKS).map(([category, links]) => (
                <div key={category} className="landing-page__footer-column">
                  <h4 className="landing-page__footer-column-title">{category}</h4>
                  <ul>
                    {links.map((link) => (
                      <li key={link}>
                        <a href="#">{link}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="landing-page__footer-bottom">
            <p>&copy; {new Date().getFullYear()} TaskLync by HKCL. All rights reserved.</p>
            <div className="landing-page__footer-bottom-links">
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
              <a href="#">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
